import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	AlertColor,
	Container,
	FormControlLabel,
	Snackbar,
	Switch,
	TextField,
	Box,
	Typography,
	Paper,
	Grid,
	InputAdornment,
	Fab,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import QuotationListItem from 'features/quotation/components/QuotationListItem';

import { Layout } from 'features/common/components/Layout';
import { listQuotation } from '../../features/quotation/services';
import { QuotationsListItem } from 'features/quotation/types';
import type { QuotationFindSimilarFilter } from '../../features/quotation/components/QuotationsSimilarFilter';
import routes from 'constants/routes';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { QuotationsSimilarFilter } from '../../features/quotation/components/QuotationsSimilarFilter';
import { CopyQuotationDialog } from '../../features/quotation/components/CopyQuotationDialog';
import { cleanRoomDistributionFilters } from '../../features/quotation/helpers';
import { PageNoResult } from '../../features/common/components/PageNoResults';
//import QuotationStats from '../../features/quotation/components/QuotationStats';

const useStyles = makeStyles((theme) => ({
	container: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(3),
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	headerSection: {
		background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
		color: 'white',
		padding: theme.spacing(4),
		borderRadius: theme.spacing(2),
		marginBottom: theme.spacing(3),
		boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
	},
	headerTitle: {
		fontWeight: 'bold',
		color: 'white',
		marginBottom: theme.spacing(1),
	},
	headerSubtitle: {
		color: 'rgba(255,255,255,0.8)',
		fontWeight: 'normal',
	},
	// Estilos para móvil
	mobileHeaderSection: {
		background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
		color: 'white',
		padding: theme.spacing(2),
		borderRadius: theme.spacing(2),
		marginBottom: theme.spacing(3),
		boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
		[theme.breakpoints.up('md')]: {
			display: 'none', // Ocultar en desktop
		},
	},
	desktopHeaderSection: {
		background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
		color: 'white',
		padding: theme.spacing(4),
		borderRadius: theme.spacing(2),
		marginBottom: theme.spacing(3),
		boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
		[theme.breakpoints.down('md')]: {
			display: 'none', // Ocultar en móvil
		},
	},
	statsContainer: {
		[theme.breakpoints.down('md')]: {
			display: 'none', // Ocultar estadísticas en móvil
		},
	},
	filtersCard: {
		padding: theme.spacing(3),
		borderRadius: theme.spacing(2),
		marginBottom: theme.spacing(3),
		background: 'white',
		boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
		border: '1px solid rgba(0,0,0,0.05)',
	},
	searchField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: theme.spacing(3),
			backgroundColor: '#f8f9fa',
			'&:hover': {
				backgroundColor: '#f0f1f3',
			},
			'&.Mui-focused': {
				backgroundColor: 'white',
				boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
			},
		},
	},
	switchContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
	},
	quotationsList: {
		marginTop: theme.spacing(3),
	},
	emptyStateContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px',
	},
	paginationContainer: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: theme.spacing(4),
		'& .MuiPagination-root': {
			'& .MuiPaginationItem-root': {
				borderRadius: theme.spacing(1),
				fontWeight: 'bold',
				'&.Mui-selected': {
					background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
					color: 'white',
					'&:hover': {
						background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
					},
				},
			},
		},
	},
	fab: {
		position: 'fixed',
		bottom: theme.spacing(3),
		right: theme.spacing(3),
		background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
		color: 'white',
		'&:hover': {
			background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
			transform: 'scale(1.1)',
		},
		transition: 'all 0.3s ease',
	},
}));

interface PaginationData {
	page: number;
	total: number;
}

export interface QuotationFilter {
	filter: string | null;
	onlyMyQuotations: boolean;
	daysOfCreated?: number;
}

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

interface IInfoToCopyQuotation {
	id: number;
	isOnlyFlights: boolean;
}

function QuoationsList(): JSX.Element {
	const classes = useStyles();
	const history = useHistory();
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [quotationsList, setQuotationList] = useState<QuotationsListItem[]>([]);
	const [paginationData, setPaginationData] = useState<PaginationData>({ page: 1, total: 1 });
	const [loading, setLoading] = useState<boolean>(false);
	const [inputFilter, setInputFilter] = useState<string>('');
	const [filterData, setFilterData] = useState<QuotationFilter>({
		filter: null,
		onlyMyQuotations: true,
		daysOfCreated: 60,
	});
	const [filtersSimilar, setFilterSimilar] = useState<QuotationFindSimilarFilter>({});
	const [filterBySimilar, setFilterBySimilar] = useState<boolean>(false);
	const [openDialogCopyQuotation, setOpenDialogCopyQuotation] = useState(false);
	const [infoToCopy, setInfoToCopy] = useState<IInfoToCopyQuotation | null>(null);

	const handleChangeInputFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputFilter(event.target.value);
	};

	const handleChangeOnlyMyQuotation = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterData({ ...filterData, onlyMyQuotations: event.target.checked });
	};

	const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
		setPaginationData({
			...paginationData,
			page: value,
		});
	};

	const handleClickOnCopyQuotation = (id: number, isOnlyFlights: boolean) => {
		setInfoToCopy({ id, isOnlyFlights });
		setOpenDialogCopyQuotation(true);
	};

	const handleSuccessCopyQuotation = (id: number) => {
		setAlert({ show: true, severity: 'success', message: `Cotización ${id} copiada correctamente` });
		handleCloseCopyQuotationDialog();
	};

	const handleCreateNewQuotation = () => {
		history.push(routes.quotations.new);
	};

	const quotationEditUrl = (id: number, isOnlyFlights: boolean): string =>
		isOnlyFlights
			? routes.quotations.editOnlyFlights.replace(':id', String(id))
			: routes.quotations.edit.replace(':id', String(id));

	const handleViewQuotation = (id: number, isOnlyFlights: boolean) => () => {
		history.push(quotationEditUrl(id, isOnlyFlights));
	};

	useEffect(() => {
		const filter = filterBySimilar ? filtersSimilar : filterData;
		setLoading(true);

		const fetchApi = async () => {
			try {
				const result = await listQuotation(paginationData.page - 1, filter);

				if (result.data && result.data.headers) {
					const newPage = Number(result.data.headers['X-Page']) + 1;
					const newTotal = Number(result.data.headers['X-Page-Count']);

					// Solo actualizar si los valores son diferentes para evitar re-renders innecesarios
					if (paginationData.page !== newPage || paginationData.total !== newTotal) {
						setPaginationData({
							page: newPage,
							total: newTotal,
						});
					}
				}

				setQuotationList(result.data.data);
				setLoading(false);
			} catch (error) {
				setLoading(false);
			}
		};

		fetchApi();
	}, [paginationData.page, filterData, filtersSimilar]);

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (inputFilter.trim() !== '') {
				setFilterData({ ...filterData, filter: inputFilter });
				setPaginationData({ ...paginationData, page: 1 });
			} else {
				setFilterData({ ...filterData, filter: null });
				setPaginationData({ ...paginationData, page: 1 });
			}
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [inputFilter]);

	const onClearSimilarFilter = () => {
		setFilterBySimilar(false);
		setFilterSimilar({});
		setPaginationData({ ...paginationData, page: 1 });
	};

	const onApplySimilarFilters = (filter: QuotationFindSimilarFilter) => {
		setFilterBySimilar(true);
		setPaginationData({ ...paginationData, page: 1 });
		const newFilters = {
			...filter,
			onlyMyQuotations: filterData.onlyMyQuotations,
			daysOfCreated: filterData.daysOfCreated,
		};
		if (newFilters.roomDistributions && newFilters.roomDistributions.length > 0) {
			const filterCleaned = {
				...newFilters,
				roomDistributions: cleanRoomDistributionFilters(
					newFilters.roomDistributions.filter((room) => room.totalAdults !== undefined) as any,
				),
			};
			setFilterSimilar(filterCleaned);
		} else if (newFilters.roomDistributions && newFilters.roomDistributions.length === 0) {
			const filterCleaned = { ...newFilters };
			delete filterCleaned.roomDistributions;
			setFilterSimilar(filterCleaned);
		} else {
			setFilterSimilar(newFilters);
		}
	};

	const handleCloseCopyQuotationDialog = () => {
		setInfoToCopy(null);
		setOpenDialogCopyQuotation(false);
	};

	return (
		<Layout>
			<Container component="main" maxWidth="lg" className={classes.container}>
				<Backdrop className={classes.backdrop} open={loading}>
					<CircularProgress color="inherit" />
				</Backdrop>

				{/* Header Section - Desktop */}
				<Paper className={classes.desktopHeaderSection} elevation={0}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12}>
							<Typography variant="h4" className={classes.headerTitle}>
								Gestión de Cotizaciones
							</Typography>
							<Typography variant="h6" className={classes.headerSubtitle}>
								Administra y visualiza todas tus cotizaciones
							</Typography>
						</Grid>
					</Grid>
				</Paper>

				{/* Header Section - Mobile */}
				<Paper className={classes.mobileHeaderSection} elevation={0}>
					<Typography variant="h5" className={classes.headerTitle}>
						Gestión de Cotizaciones
					</Typography>
				</Paper>

				{/* Estadísticas Inteligentes - Solo Desktop */}
				<Box className={classes.statsContainer}>
					{/* Protección adicional: si QuotationStats falla, no afecta nada */}
					{/* {(() => {
						try {
							return <QuotationStats />;
						} catch (error) {
							console.warn('Error cargando estadísticas, continuando sin ellas:', error);
							return null;
						}
					})()} */}
				</Box>

				{/* Filters Section */}
				<Paper className={classes.filtersCard}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} md={8}>
							<TextField
								fullWidth
								className={classes.searchField}
								value={inputFilter}
								label="Buscar cotizaciones"
								placeholder="Busca por ID, ticket CRM, nombre, teléfono, descripción o email"
								onChange={handleChangeInputFilter}
								variant="outlined"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon color="action" />
										</InputAdornment>
									),
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<Box className={classes.switchContainer}>
								<FormControlLabel
									control={
										<Switch
											checked={filterData.onlyMyQuotations}
											name="onlyMyQuotations"
											onChange={handleChangeOnlyMyQuotation}
											color="primary"
										/>
									}
									label="Solo mis cotizaciones"
								/>
							</Box>
						</Grid>
					</Grid>
				</Paper>

				<QuotationsSimilarFilter
					setLoading={setLoading}
					onClearFilter={onClearSimilarFilter}
					onApplyFilter={onApplySimilarFilters}
				/>

				{/* Quotations List */}
				<Box className={classes.quotationsList}>
					{quotationsList.length === 0 ? (
						<Box className={classes.emptyStateContainer}>
							<PageNoResult />
						</Box>
					) : (
						<Grid container spacing={2}>
							{quotationsList.map((quotation) => (
								<Grid item xs={12} key={quotation.id}>
									<QuotationListItem
										data={quotation}
										href={quotationEditUrl(quotation.id, quotation.isOnlyFlights)}
										onClick={handleViewQuotation(quotation.id, quotation.isOnlyFlights)}
										onDuplicate={() => handleClickOnCopyQuotation(quotation.id, quotation.isOnlyFlights)}
									/>
								</Grid>
							))}
						</Grid>
					)}
				</Box>

				{/* Pagination */}
				{quotationsList.length > 0 && (
					<Box className={classes.paginationContainer}>
						<Pagination
							count={paginationData.total}
							page={paginationData.page}
							variant="outlined"
							color="primary"
							size="large"
							onChange={handleChangePage}
						/>
					</Box>
				)}

				{/* Floating Action Button */}
				<Fab
					className={classes.fab}
					color="primary"
					aria-label="crear nueva cotización"
					onClick={handleCreateNewQuotation}
				>
					<AddIcon />
				</Fab>

				<Snackbar open={alert.show} autoHideDuration={1000} onClose={() => setAlert(defaultAlertState)}>
					<Alert variant="filled" severity={alert.severity}>
						{alert.message}
					</Alert>
				</Snackbar>
				{openDialogCopyQuotation && infoToCopy && (
					<CopyQuotationDialog
						quotationIdToCopy={infoToCopy.id}
						isOnlyFlights={infoToCopy.isOnlyFlights}
						open={true}
						onClose={handleCloseCopyQuotationDialog}
						onSuccessCopyQuotation={handleSuccessCopyQuotation}
					/>
				)}
			</Container>
		</Layout>
	);
}

export default QuoationsList;
